import { AccountFeatureFlag } from '@/services/auth/feature-flags';
import { generatePath } from '@/services/router/generate-path';
import { registerSiblingPreload } from '@/services/router/sibling-preload';

/**
 * The campaign overview page. Its Workflow tab moved to this app behind
 * `workflow-builder-migration` (FPS-2338) while its six siblings stayed with the
 * embedded app, so the common path onto the canvas is a click from an
 * embedded-app tab.
 *
 * Both URL spellings are matched — `campaigns` redirects to `automations`, so both
 * are live URLs — and the campaign id is bounded to digits, as campaign ids are
 * numeric.
 */
const CAMPAIGN_OVERVIEW = /^\/workspaces\/(last|\d+)\/journeys\/(?:campaigns|automations)\/(\d+)\/overview(?:[/?#]|$)/i;

/**
 * A person profile tab the embedded app still serves. The profile's Attributes, Relationships, Segments, Devices, Drafts, Sent and Activity tabs
 * moved to this app behind `profile-page-migration` (FPS-4208, FPS-2712) while their siblings
 * stayed embedded, so the common path onto them is a click from an embedded-app tab. The person id is any one segment: a
 * profile is addressed by its internal id or by an external id. `new`, `merge`, `create` and
 * `welcome` are People pages, not people, so they are excluded.
 */
const PERSON_PROFILE =
  /^\/workspaces\/(last|\d+)\/journeys\/people\/(?!(?:new|merge|create|welcome)(?:[/?#]|$))([^/?#]+)(?:[/?#]|$)/i;

/** The URL segment decoded, or `null` when it is not valid percent-encoding. */
function decodeSegment(segment: string): string | null {
  try {
    return decodeURIComponent(segment);
  } catch {
    return null;
  }
}

/**
 * Register every sibling preload. Called from `main.tsx`, next to
 * `installLinkPrefetching`: the entry module runs before `render`, and the shell
 * preloads on its first location effect, so an entry registered any later would
 * miss the load the user actually landed on.
 */
export function registerSiblingPreloads() {
  registerSiblingPreload({
    when: PERSON_PROFILE,
    flag: AccountFeatureFlag.ProfilePageMigration,

    siblings: (match) => {
      const [, workspaceId, personId] = match;
      return [
        generatePath('/workspaces/:workspaceId/people/:personId/attributes', { workspaceId, personId }),
        generatePath('/workspaces/:workspaceId/people/:personId/relationships', { workspaceId, personId }),
        generatePath('/workspaces/:workspaceId/people/:personId/segments/belongs', { workspaceId, personId }),
        generatePath('/workspaces/:workspaceId/people/:personId/devices', { workspaceId, personId }),
        generatePath('/workspaces/:workspaceId/people/:personId/drafts', { workspaceId, personId }),
        generatePath('/workspaces/:workspaceId/people/:personId/sent', { workspaceId, personId }),
        generatePath('/workspaces/:workspaceId/people/:personId/activity', { workspaceId, personId }),
      ];
    },

    prefetchData: async ({ queryClient, match }) => {
      const [, workspaceSegment, personSegment] = match;
      // The page reads the person by the decoded id, so an encoded external id must be decoded
      // to warm the same cache key.
      const personId = personSegment == null ? null : decodeSegment(personSegment);
      // `last` is resolved by the workspace resolver, not here; with no numeric id there is no
      // cache key to write, so the chunk is all this can warm.
      if (workspaceSegment == null || personId == null || workspaceSegment.toLowerCase() === 'last') return;

      // Imported here so the shell entry bundle does not grow by the customer data layer.
      const { getCustomerQueryOptions } = await import('@/services/db/customer/queries');
      await queryClient.prefetchQuery(getCustomerQueryOptions(Number(workspaceSegment), personId));
    },
  });

  registerSiblingPreload({
    when: CAMPAIGN_OVERVIEW,
    flag: AccountFeatureFlag.WorkflowBuilderMigration,

    siblings: (match) => {
      const [, workspaceId, campaignId] = match;
      // The canonical address. The legacy `.../overview/workflow/actions` twin forwards
      // to it, so one href covers both: the chunk loader and the query cache are
      // shared, and ownership answers the same for either shape.
      return [generatePath('/workspaces/:workspaceId/automations/:campaignId/workflow', { workspaceId, campaignId })];
    },

    prefetchData: async ({ queryClient, match }) => {
      const [, workspaceSegment, campaignId] = match;
      // `last` is resolved by the workspace resolver, not here; with no numeric
      // id there is no cache key to write, so the chunk is all this can warm.
      if (workspaceSegment == null || campaignId == null || workspaceSegment.toLowerCase() === 'last') return;

      // Imported here rather than at module scope so the shell entry bundle does
      // not grow by the campaign data layer, and so an account with the rollout
      // flag off never loads it at all.
      const { getCampaignWorkflowDetailQueryOptions } = await import('@/services/db/campaign/queries');

      // One `GET /campaigns/:id`. Its `populateCampaignModel` fans the envelope
      // into `campaignKeys.detail` and every action detail slot as well as the
      // workflow-detail slot, so this single prefetch also satisfies `useCampaign`
      // and `useCampaignActions` — the page's other two suspense sources.
      await queryClient.prefetchQuery(
        getCampaignWorkflowDetailQueryOptions(Number(workspaceSegment), campaignId, queryClient),
      );
    },
  });
}
